/*
 * 拉当前 session 的 context_usage (对齐 XHeyClaw-Web/lib/use-session-context-usage.ts)
 *
 * 接口契约:
 *   GET /api/sessions/{sessionId}/context-usage?group_id=<gid>
 *     → { context_usage: {...} | null }
 *   (本地走 xheyAi.getSessionContextUsageRaw, 由 api.ts request() 自动注入
 *    Authorization Bearer + x-group-id, 走 /xhey-ai/* setupProxy)
 *
 * 触发更新:
 *   - 切换 sessionId / 启用状态变化时 fetch 一次
 *   - 监听 window 'claw-thread-update' 事件 (流结束 / 用户提交 user-ask 后业务侧 dispatch)
 *     200ms 防抖后再 fetch, 跟 XHeyClaw-Web 同步 (旧 1200ms 用户感觉滞后)
 */
import { useEffect, useState } from 'react'
import { xheyAi } from '../api'
import { getSessionContextUsage } from './contextUsage'
import type { SessionContextUsage } from './contextUsage'

const SESSION_CONTEXT_REFRESH_DELAY_MS = 200

export function useSessionContextUsage(params: {
  groupId: string
  userId?: string
  conversationId?: string
  sessionId?: string
  enabled?: boolean
}): SessionContextUsage | null {
  const { groupId, conversationId, sessionId, enabled = true } = params
  const [usage, setUsage] = useState<SessionContextUsage | null>(null)

  useEffect(() => {
    const targetSessionId = sessionId || conversationId
    if (!enabled || !groupId || !targetSessionId) {
      setUsage(null)
      return
    }

    let cancelled = false
    let controller: AbortController | null = null
    let refreshTimer: ReturnType<typeof setTimeout> | null = null

    const fetchUsage = async () => {
      if (controller) {
        controller.abort()
      }
      controller = new AbortController()
      try {
        const data = await xheyAi.getSessionContextUsageRaw(
          targetSessionId,
          controller.signal
        )
        if (cancelled) return
        setUsage(getSessionContextUsage(data))
      } catch (err) {
        if ((err as { name?: string })?.name !== 'AbortError' && !cancelled) {
          setUsage(null)
        }
      }
    }

    const scheduleRefresh = () => {
      if (refreshTimer) clearTimeout(refreshTimer)
      refreshTimer = setTimeout(() => {
        refreshTimer = null
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        fetchUsage().catch(() => {})
      }, SESSION_CONTEXT_REFRESH_DELAY_MS)
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    fetchUsage().catch(() => {})
    window.addEventListener('claw-thread-update', scheduleRefresh)
    return () => {
      cancelled = true
      if (controller) {
        controller.abort()
      }
      if (refreshTimer) clearTimeout(refreshTimer)
      window.removeEventListener('claw-thread-update', scheduleRefresh)
    }
  }, [conversationId, enabled, groupId, sessionId])

  return usage
}
